/**
 * 네이버웍스 캘린더 API 클라이언트 — backend `/calendar/*` 와 1:1 (v2.139.0)
 *
 * v2.138.0 까지는 읽기 전용이었다. v2.139.0 에서 **등록·수정·삭제**가 열렸다.
 * 다만 영업진행이 만든 납품 일정(`source: 'sales'`)은 여전히 **여기서 못 고친다** —
 * 그 일정은 영업건의 납품예정일에서 자동으로 만들어지므로 영업진행 화면에서 고쳐야 한다.
 */

import { api } from './api';

/**
 * 이 일정이 어디서 왔나 — 서버가 **번호로** 판정한다(제목 글자로 판정하지 않는다)
 * `hulam` = 휴램 휴가 신청·최종 승인으로 자동 등록된 일정(v2.214.0 · 납품처럼 여기서 못 고친다 — 이유는 lockReason)
 */
export type WorksEventSource = 'sales' | 'hulam' | 'portal' | 'works';

export interface WorksEvent {
  eventId: string;
  summary: string;
  /** 시작일 'YYYY-MM-DD' */
  date: string;
  /** 마지막 날 'YYYY-MM-DD' (하루짜리면 date 와 같다) */
  endDate: string;
  allDay: boolean;
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  description: string | null;
  viewUrl: string | null;
  source: WorksEventSource;
  /** 포털에서 만든 일정이면 등록자 이름 */
  createdByName: string | null;
  canEdit: boolean;
  /** 못 고치는 이유 — 화면이 그대로 보여 준다 */
  lockReason: string | null;
}

export interface WorksEventsResult {
  /** 서버에 캘린더 설정이 없으면 false — 화면이 「연동 전」으로 안내한다 */
  enabled: boolean;
  events: WorksEvent[];
  from: string;
  to: string;
  /** 31일을 넘겨 서버가 기간을 잘랐나 */
  clamped: boolean;
  /**
   * 이 계정이 일정을 만들 수 있나.
   * ⭐v2.187.0 부터 기준은 **역할이 아니라 「본인 웍스 계정을 연결했나」**다(viewer 포함).
   */
  canCreate: boolean;
  /** 연결 안 했으면 화면이 「연결하세요」를 띄운다 (v2.187.0) */
  calendarLinked?: boolean;
}

/* ── 내 웍스 계정 연결 (v2.187.0) ─────────────────────────
 * 「작성자는 각자 계정으로」를 위해 사람마다 본인 웍스 계정을 한 번 연결한다.
 * 토큰은 **서버에만** 암호화 저장된다 — 브라우저로 내려오지 않는다. */

export interface CalendarAccountStatus {
  /** 서버에 앱 설정이 있나 (관리자 작업) */
  configured: boolean;
  connected: boolean;
  /** 연결이 만료됐나 — 다시 연결해야 한다 */
  expired: boolean;
  nwEmail: string | null;
  connectedAt: string | null;
}

export function getCalendarAccount(): Promise<CalendarAccountStatus> {
  return api.get<CalendarAccountStatus>('/calendar/account');
}

/** 연결 시작 — 받은 주소로 브라우저를 옮기면 웍스 동의 화면이 뜬다 */
export function connectCalendarAccount(): Promise<{ authorizeUrl: string }> {
  return api.post<{ authorizeUrl: string }>('/calendar/account/connect', {});
}

export function disconnectCalendarAccount(): Promise<{ disconnected: boolean }> {
  return api.delete<{ disconnected: boolean }>('/calendar/account');
}

/** 등록·수정 입력 — 화면 입력 그대로. 검사는 서버가 한다(문장으로 돌려준다). */
export interface WorksEventInput {
  summary: string;
  date: string;
  endDate?: string | null;
  allDay: boolean;
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  description?: string | null;
}

export function listWorksEvents(from: string, to: string): Promise<WorksEventsResult> {
  const qs = new URLSearchParams({ from, to });
  return api.get<WorksEventsResult>(`/calendar/events?${qs.toString()}`);
}

export function createWorksEvent(input: WorksEventInput): Promise<{ eventId: string }> {
  return api.post<{ eventId: string }>('/calendar/events', input);
}

/** 고치기 — 웍스가 수정을 거절하면 서버가 우회하므로 **일정 번호가 바뀔 수 있다** */
export function updateWorksEvent(
  eventId: string,
  input: WorksEventInput,
): Promise<{ eventId: string; warning: string | null }> {
  return api.patch<{ eventId: string; warning: string | null }>(
    `/calendar/events/${encodeURIComponent(eventId)}`,
    input,
  );
}

export function deleteWorksEvent(eventId: string): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/calendar/events/${encodeURIComponent(eventId)}`);
}
